import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { motion } from "framer-motion";
import { ForwardedRef, forwardRef, ReactNode } from "react";

import { Link } from "./Link.tsx";
import { TouchTarget } from "./TouchTarget.tsx";

export const SidebarItem = forwardRef(function SidebarItem(
  {
    children,
    className,
    contentClassName,
    current,
    ...props
  }: {
    children: ReactNode;
    className?: string;
    contentClassName?: string;
    current?: boolean;
  } & (
    | Omit<Headless.ButtonProps, "as" | "className">
    | Omit<Headless.ButtonProps<typeof Link>, "as" | "className">
  ),
  ref: ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const classes = clsx(
    // Base
    "flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-normal text-zinc-950 sm:py-2 sm:text-sm/5",
    // Leading icon/icon-only (outline icons use stroke)
    "data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:stroke-zinc-500 sm:data-[slot=icon]:*:size-5",
    // Trailing icon (down chevron or similar)
    "data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-5 sm:data-[slot=icon]:last:*:size-4",
    // Avatar
    "data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6",
    // Hover
    "data-[hover]:bg-zinc-950/5 data-[slot=icon]:*:data-[hover]:stroke-zinc-950",
    // Active
    "data-[active]:bg-zinc-950/5 data-[slot=icon]:*:data-[active]:stroke-zinc-950",
    // Current
    "data-[slot=icon]:*:data-[current]:stroke-zinc-950",
    // Dark mode
    "dark:text-white dark:data-[slot=icon]:*:stroke-zinc-400",
    "dark:data-[hover]:bg-white/5 dark:data-[slot=icon]:*:data-[hover]:stroke-white",
    "dark:data-[active]:bg-white/5 dark:data-[slot=icon]:*:data-[active]:stroke-white",
    "dark:data-[slot=icon]:*:data-[current]:stroke-white",
  );

  return (
    <span className={clsx(className, "relative")}>
      {current && (
        <motion.span
          className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-zinc-950 dark:bg-white"
          layoutId="current-indicator"
        />
      )}
      {"href" in props ? (
        <Headless.Button
          as={Link}
          {...props}
          className={clsx(contentClassName, classes)}
          data-current={current ? "true" : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      ) : (
        <Headless.Button
          {...props}
          className={clsx(contentClassName, classes)}
          data-current={current ? "true" : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});
